import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectDataActiveMeasures } from '../../reducers/dataSelectors';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { CurrencyDisplay } from './display/CurrencyDisplay';
import { ActiveMeasureItem } from './ActiveMeasureItem';
import { Tooltip } from 'react-tooltip';
import { usePreferences } from '../../context/PreferencesContext';

/* =========================================================
   Preference configuration (UI order == multiCriteria order)
   ========================================================= */

const preferenceConfig = [
  {
    key: 'thermal',
    label: 'Thermischer Komfort',
    children: ['Raumtemperatur', 'Sonnen- und Blendschutz', 'Strahlung'],
  },
  {
    key: 'air',
    label: 'Raumluft-Qualität',
    children: [
      'Luftwechsel',
      'Raumluftfeuchte',
      'Schadstoffe',
      'Geruchsvermeidung',
      'Radon',
    ],
  },
  {
    key: 'hotwater',
    label: 'Brauchwarmwasser',
    children: ['Solltemperatur', 'Warmhaltung', 'Hygiene', 'Kalk'],
  },
  {
    key: 'visual',
    label: 'Visueller Komfort',
    children: ['Tageslicht', 'Kunstlicht', 'Gestaltungsqualität', 'Privatsphäre'],
  },
  {
    key: 'acoustic',
    label: 'Akustischer Komfort',
    children: ['Raumakustik', 'Interne Lärmemissionen', 'Externe Lärmemissionen'],
  },
  {
    key: 'usage',
    label: 'Gebrauchs-/Aufenthaltsqualität',
    children: [
      'Identifikation',
      'Sicherheitsgefühl',
      'Soziale Interaktion',
      'Aneignung',
      'Gebäudeautomation',
      'Materialität',
      'Vegetation',
      'Nutzungsflexibilität',
      'Raumqualität',
    ],
  },
];

const categoryColors = {
  thermal: 'linear-gradient(to bottom, #ff6b6b, #c92a2a)', // red
  air: 'linear-gradient(to bottom, #66d9e8, #1098ad)', // cyan
  hotwater: 'linear-gradient(to bottom, #2b896eff, #06634dff)', // neutral
  visual: 'linear-gradient(to bottom, #ffd43b, #f59f00)', // yellow
  acoustic: 'linear-gradient(to bottom, #aa95b3ff, #5a0877ff)', // purple
  usage: 'linear-gradient(to bottom, #f783ac, #a61e4d)', // pink
};

/* =========================================================
   Helpers
   ========================================================= */

const isOne = (v) => v === '1' || v === 1 || v === true;

const getSelectedOptionFromMeasure = (measure) => {
  const sm = measure?.subMeasure?.[0];
  if (!sm?.value || !Array.isArray(sm.subOption)) return null;
  return sm.subOption.find((opt) => opt.uid === sm.value) || null;
};

/* =========================================================
   Tooltip content (PER MEASURE)
   ========================================================= */

const PreferencesTooltipContent = ({ preferences, measure }) => {
  if (!preferences) return null;

  if (!measure) {
    return <div style={{ minWidth: 360, fontSize: 13 }}>—</div>;
  }

  const selectedOpt = getSelectedOptionFromMeasure(measure);

  if (!selectedOpt?.multiCriteria) {
    return (
      <div style={{ minWidth: 360 }}>
        <div className="fw-bold mb-2" style={{ fontSize: 13 }}>
          {measure.displayText || 'Massnahme'}
        </div>
        <div style={{ fontSize: 13 }}>Keine Variante gewählt.</div>
      </div>
    );
  }

  const categories = preferenceConfig.map((section, rowIndex) => {
    const state = preferences[section.key];
    const enabled = !!state?.checked;

    const max = section.children.length;
    let score = 0;

    for (let i = 0; i < max; i++) {
      const childLabel = section.children[i];

      // checkbox selected by label (UI order)
      const prefSelected = !!state?.children?.[childLabel];

      // multiCriteria match (same index)
      const allowed = isOne(selectedOpt.multiCriteria?.[rowIndex]?.[i]);

      if (prefSelected && allowed) score += 1;
    }

    return {
      key: section.key,
      label: section.label,
      score,
      max,
      enabled,
    };
  });

  return (
    <div style={{ minWidth: 360 }}>
      <div className="fw-bold mb-2" style={{ fontSize: 13 }}>
        {measure.displayText || 'Massnahme'}
        {selectedOpt?.displayText ? ` — ${selectedOpt.displayText}` : ''}
      </div>

      <div className="fw-bold d-flex justify-content-between mb-2">
        <span>Zusatznutzen</span>
        <span>Score</span>
      </div>

      {categories.map((cat) => {
        // width is relative to this category's own max, so 1/3 -> 33%
        const widthPercent = cat.max > 0 ? (cat.score / cat.max) * 100 : 0;

        return (
          <div
            key={cat.key}
            className="d-flex align-items-center mb-2"
            style={{ fontSize: 13 }}
          >
            {/* LABEL */}
            <div style={{ minWidth: 190, paddingRight: 6, lineHeight: 1.2 }}>
              {cat.label}
            </div>

            {/* BAR */}
            <div
              style={{
                flexGrow: 1,
                height: 14,
                background: '#e9ecef',
                marginRight: 6,
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${widthPercent}%`,
                  background: cat.enabled
                    ? categoryColors[cat.key]
                    : '#dcdcdcff',
                }}
              />
            </div>

            {/* SCORE */}
            <div
              style={{
                width: 52,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {cat.score}/{cat.max}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* =========================================================
   Component
   ========================================================= */

export const ActiveMeasureList = () => {
  const activeMeasures = useSelector(selectDataActiveMeasures);
  const { preferences } = usePreferences();

  // store only the parameter to avoid stale object references
  const [hoveredMeasureParam, setHoveredMeasureParam] = useState(null);

  // Always call hooks unconditionally (no early return above this point)
  const hoveredMeasure = useMemo(() => {
    if (!hoveredMeasureParam) return null;
    if (!Array.isArray(activeMeasures)) return null;
    return activeMeasures.find((m) => m.parameter === hoveredMeasureParam) || null;
  }, [activeMeasures, hoveredMeasureParam]);

  // Key forces Tooltip remount when activeMeasures changes (e.g., selection changes)
  const tooltipKey = useMemo(() => {
    if (!Array.isArray(activeMeasures)) return 'no-measures';
    return activeMeasures
      .map((m) => `${m.parameter}:${m.subMeasure?.[0]?.value ?? ''}`)
      .join('|');
  }, [activeMeasures]);

  // Now it’s safe to early-return
  if (!activeMeasures) return '';

  const printHeaderRow = () => {
    return (
      <Row
        className={'small fw-bold border-bottom border-2 mb-3 pb-1'}
        style={{ fontSize: '12px' }}
      >
        <Col xs={6} md={4} className={'pe-0'}>
          Massnahme
        </Col>
        <Col xs={6} md={4}>
          Variante
        </Col>
        <Col
          xs={{ span: 3, offset: 6 }}
          md={{ span: 2, offset: 0 }}
          lg={3}
          xl={2}
          className={'d-flex justify-content-end px-0'}
        >
          Brutto <br />
          Investment
        </Col>
      </Row>
    );
  };

  const printTotalRow = () => {
    return (
      <Row className={'fw-bold border-top pt-3 mt-3 border-2'}>
        <Col xs={6} md={4}>
          Total
        </Col>
        <Col xs={6} md={4}></Col>

        <Col
          xs={{ span: 3, offset: 6 }}
          md={{ span: 2, offset: 0 }}
          lg={3}
          xl={2}
          className={'d-flex justify-content-end px-0'}
        >
          <CurrencyDisplay showZero={true}>
            {activeMeasures.reduce((acc, curr) => {
              const sm = curr?.subMeasure?.[0];
              const sel = sm?.subOption?.find((x) => x.uid === sm?.value);
              return acc + Number(sel?.cost?.grossInvestment || 0);
            }, 0)}
          </CurrencyDisplay>
        </Col>
      </Row>
    );
  };

  let content = '';
  if (activeMeasures.length === 0) {
    content = (
      <div>
        <i>keine Massnahmen ausgewählt</i>
      </div>
    );
  } else {
    content = (
      <div>
        {/* Single tooltip instance; key forces remount/recalc on activeMeasures changes */}
        <Tooltip
          key={tooltipKey}
          id="measure-tooltip"
          place="right"
          clickable
          disableStyleInjection={false}
          style={{
            backgroundColor: '#fff',
            color: '#212529',
            border: '1px solid #dee2e6',
            borderRadius: 6,
            padding: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
          }}
          render={() => (
            <PreferencesTooltipContent
              preferences={preferences}
              measure={hoveredMeasure}
            />
          )}
        />

        {printHeaderRow()}

        {activeMeasures.map((itm) => (
          <div
            key={itm.parameter}
            data-tooltip-id="measure-tooltip"
            data-tooltip-delay-show={150}
            onMouseEnter={() => setHoveredMeasureParam(itm.parameter)}
            onFocus={() => setHoveredMeasureParam(itm.parameter)}
          >
            <ActiveMeasureItem item={itm} />
          </div>
        ))}

        {printTotalRow()}
      </div>
    );
  }

  return <div>{content}</div>;
};
